import React, { useState, useEffect } from "react";
import swal from "sweetalert";
import {
  getCirculars,
  deleteCircular,
  toggleCircularStatus,
} from "../../../services/adminApiService/circularApi/circularApi";
import CircularModal from "./CircularModal";
import PaginationComp from "../../../common/ui/view/pagination/PaginationComp";

const CircularList = () => {
  const [data, setData] = useState([]);
  const [totalPages, setTotalPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [selectedCircular, setSelectedCircular] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchData(currentPage, search);
  }, [currentPage]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchData(1, search);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  async function fetchData(page, searchTerm) {
    setLoading(true);
    try {
      const res = await getCirculars(page, searchTerm);
      setTotalPages(res?.data?.data?.last_page);
      setData(res?.data?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  const handleDelete = (id) => {
    swal({
      title: "Are you sure?",
      text: "This will permanently delete the circular and its file.",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willDelete) => {
      if (willDelete) {
        try {
          await deleteCircular(id);
          swal("Deleted!", "Circular deleted.", "success");
          fetchData(currentPage, search);
        } catch {
          swal("Error", "Failed to delete.", "error");
        }
      }
    });
  };

  const handleToggleStatus = async (id) => {
    try {
      await toggleCircularStatus(id);
      fetchData(currentPage, search);
    } catch {
      swal("Error", "Failed to update status.", "error");
    }
  };

  const handleModalClose = (refresh = false) => {
    setShowModal(false);
    setSelectedCircular(null);
    if (refresh) fetchData(currentPage, search);
  };

  const formatDate = (dt) =>
    dt ? new Date(dt).toLocaleString("en-IN") : "—";

  return (
    <div className="container-fluid py-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">Government Circulars</h4>
        <button
          className="btn btn-primary"
          onClick={() => {
            setSelectedCircular(null);
            setShowModal(true);
          }}
        >
          + Add Circular
        </button>
      </div>

      <div className="mb-3">
        <input
          type="text"
          className="form-control"
          placeholder="Search by title..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 400 }}
        />
      </div>

      <div className="table-responsive">
        <table className="table table-bordered table-hover">
          <thead style={{ backgroundColor: "rgba(12,46,94,1)", color: "#fff" }}>
            <tr>
              <th>#</th>
              <th>Title</th>
              <th>Description</th>
              <th>Start Date</th>
              <th>End Date</th>
              <th>Attachment</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="text-center">Loading...</td>
              </tr>
            ) : data.length === 0 ? (
              <tr>
                <td colSpan={8} className="text-center">No circulars found.</td>
              </tr>
            ) : (
              data.map((item, idx) => (
                <tr key={item.id}>
                  <td>{(currentPage - 1) * 10 + idx + 1}</td>
                  <td>{item.title}</td>
                  <td style={{ maxWidth: 200 }}>
                    <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {item.description || "—"}
                    </div>
                  </td>
                  <td>{formatDate(item.start_date_time)}</td>
                  <td>{formatDate(item.end_date_time)}</td>
                  <td>
                    {item.attachement_storage_url ? (
                      <a
                        href={item.attachement_storage_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-sm btn-outline-primary"
                      >
                        View PDF
                      </a>
                    ) : "—"}
                  </td>
                  <td>
                    <span
                      className={`badge ${item.status ? "bg-success" : "bg-secondary"}`}
                      style={{ cursor: "pointer" }}
                      onClick={() => handleToggleStatus(item.id)}
                      title="Click to toggle"
                    >
                      {item.status ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <button
                      className="btn btn-sm btn-warning me-2"
                      onClick={() => {
                        setSelectedCircular(item);
                        setShowModal(true);
                      }}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-sm btn-danger"
                      onClick={() => handleDelete(item.id)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <PaginationComp
          size={totalPages}
          currentpage={currentPage}
          setCurrentPage={setCurrentPage}
        />
      )}

      {showModal && (
        <CircularModal circular={selectedCircular} onClose={handleModalClose} />
      )}
    </div>
  );
};

export default CircularList;
